/* eslint-disable max-lines */
import React, { useEffect, useMemo, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { CollectScreenConfiguration, ConsentConfiguration } from '@luckycart/xpn-types';

import { CollectHtmlZone, DeclineCtaButton } from './CollectScreen.style';
import { Background } from '../common-components/Background';
import { BlockInfoType } from '../../common-types/BlockInfo.type';
import { CtaButton } from '../common-components/CtaButton';
import { Consents } from '../common-components/Consents';
import { PersonalData } from '../common-components/PersonalData';
import { CollectFormValues } from '../../common-types/CollectForm.type';
import { Loader } from '../common-components/Loader';
import { useAppSelector } from '../../hooks';

// eslint-disable-next-line
const EMAIL_REGX = /^(?!.*\.\.)[a-zA-Z0-9](?:[a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/;
const INVALID_EMAIL_ERROR_MESSAGE = 'Veuillez saisir une adresse e-mail valide !';
const LECLERC_SITE_KEY = 'Dwj83zVC';

interface Props {
  suOptinForm: boolean;
  siteKey: string | null;
  config: CollectScreenConfiguration;
  addEditableBlock?: (block: BlockInfoType) => void;
  isEdition?: boolean;
  onBlockEdit?: React.MouseEventHandler<HTMLDivElement>;
  ctaOnClick?: () => void;
  onSubmit?: (values: CollectFormValues) => void;
  className?: string;
  checked?: React.ChangeEventHandler<HTMLInputElement>;
}

export const CollectScreen: React.FunctionComponent<Props> = ({
  suOptinForm,
  siteKey,
  addEditableBlock,
  config,
  isEdition,
  onBlockEdit,
  ctaOnClick,
  onSubmit,
  className,
} : Props) => {
  const {
    retailerPersonalData,
  } = useAppSelector((state) => state.retailerPersonalData);
  const {
    smartOptinsExperience,
    hasSmartOptinsExperience,
  } = useAppSelector((state) => state.smartOptins);
  const {
    site,
  } = useAppSelector((state) => state.globalExperience);
  // let's discuss it for smart optins:
  //  - when I got en email (only asking an email) and no optins to ask (from retailerData)
  //    => auto validate to go to next screen
  const [autoSubmitChecked, setAutoSubmitChecked] = useState<boolean>(false);
  const [smartOptinAccepted, setSmartOptinAccepted] = useState<boolean>(false);
  const [optinsKeysAccepted, setOptinsKeysAccepted] = useState<string[]>([]);

  const handleOptinsKeysAcceptedChange = (newOptinsKeysAccepted: string[]): void => {
    if (newOptinsKeysAccepted.length) {
      setSmartOptinAccepted(true);
    }
    setOptinsKeysAccepted(newOptinsKeysAccepted);
  };

  const personalDataShape = useMemo(() => {
    const shape: Record<string, Yup.AnySchema> = {};
    const filteredPersonalData = config.personalData.filter(({ activated }) => activated);
    for (const { type, key, required, msgError } of filteredPersonalData) {
      switch (type) {
      case 'email':
        shape[key] = Yup.string().required(INVALID_EMAIL_ERROR_MESSAGE).matches(EMAIL_REGX, INVALID_EMAIL_ERROR_MESSAGE);
        break;
      default:
        shape[key] = Yup.mixed();
        break;
      }
      if (required === true) {
        shape[key] = shape[key].required( msgError ?? INVALID_EMAIL_ERROR_MESSAGE);
      }
    }
    return shape;
  }, [config.personalData]);

  const userConsents = useMemo<ConsentConfiguration[]>(() => {
    let optinListToReturn: ConsentConfiguration[] = [];
    if (site?.isSmartOptinsActive === true && (suOptinForm || siteKey === LECLERC_SITE_KEY)) {
      if (siteKey === LECLERC_SITE_KEY) {
        return config.consents.filter(({ key }) => retailerPersonalData?.optinList?.some((optin) => optin.key === key));
      }
      if (suOptinForm) {
        const emailConfigConsent = [config.consents[0]];
        const smsConfigConsent = [config.consents[1]];
        if (retailerPersonalData?.optinList) {
          return retailerPersonalData.optinList[0].name === 'email' ? emailConfigConsent : smsConfigConsent;
        }
      }
      const retailerOptinKeys = retailerPersonalData === null || retailerPersonalData.optinList === null
        ? null
        : retailerPersonalData.optinList.map((optin) => optin.key);
      if (retailerOptinKeys !== null && hasSmartOptinsExperience) {
        const optinKeyToDisplay = smartOptinsExperience?.payload.optins
          .find((experienceOptin: string) => retailerOptinKeys.find((retailerOptin) => experienceOptin === retailerOptin));
        optinListToReturn = config.consents.filter(({ activated, key }) => activated && key === optinKeyToDisplay);
      }
    } else {
      optinListToReturn = config.consents.filter(({ activated, key }) => activated || key === 'optional');
    }

    return optinListToReturn.slice(0, 1);
  }, [
    siteKey, 
    site?.isSmartOptinsActive, 
    config.consents, 
    retailerPersonalData,
    hasSmartOptinsExperience, 
    smartOptinsExperience?.payload.optins,
    suOptinForm,
  ]);

  const CollectScreenSchema = Yup.object().shape({
    personalData: Yup.object().shape(personalDataShape),
  });

  useEffect(() => {
    const hasEmailAndNoOptins = typeof onSubmit === 'function' && retailerPersonalData !== null &&
      retailerPersonalData.email !== null &&
      userConsents.length === 0;
	
    if (retailerPersonalData !== null && hasEmailAndNoOptins) {
      onSubmit({
        consents: {},
        personalData: { email: retailerPersonalData.email as string },
      });
      return;
    }
    setAutoSubmitChecked(true);
  }, [onSubmit, retailerPersonalData, userConsents, suOptinForm]);

  const handleDeclineCtaClick = ({ handleSubmit, setFieldValue }: Pick<FormikProps<CollectFormValues>, 'handleSubmit' | 'setFieldValue'>): void => {
    userConsents.forEach(({ key }) => {
      setFieldValue(`consents.${key}`, false);
      /** For SU here, we have to modify the onSubmit journey because no input form are changing
	   * So just fill the onSubmit function with the right values
	   */
      if (onSubmit !== undefined && suOptinForm) {
        onSubmit({
          consents: { [key]: false },
          personalData: { email: retailerPersonalData?.email as string, phone: retailerPersonalData?.phone as string },
        });
      }
    });
    handleSubmit();
  };

  const handleAcceptCtaClick = ({ handleSubmit, setFieldValue }: Pick<FormikProps<CollectFormValues>, 'handleSubmit' | 'setFieldValue'>): void => {
    userConsents.forEach(({ key }) => {
      setFieldValue(`consents.${key}`, true);
      /** For SU here, we have to modify the onSubmit journey because no input form are changing
	   * So just fill the onSubmit function with the right values
	   */
      if (onSubmit !== undefined && suOptinForm) {
        onSubmit({
          consents: { [key]: true },
          personalData: { email: retailerPersonalData?.email as string, phone: retailerPersonalData?.phone as string },
        });
      }
    });
    handleSubmit();
  };

  const isOptinChecked = (evt: boolean): boolean => {
    if (evt) {
      setSmartOptinAccepted(true);
      return true;
    }
    setSmartOptinAccepted(false);
    return false;
  };

  const isHiddenForCheckbox = (): boolean => {
    if (userConsents.find(({ type }) => type === 'checkbox' || type === 'toggle')) {
      return !smartOptinAccepted;
    }
    return false;
  };
  const isConsentsDisabled = (values: CollectFormValues) : boolean => {
    const email = config.personalData.find(({ key, activated, required }) => key === 'email' && activated === true && required === true);
    return email === undefined
      ? false
      : values.personalData.email === undefined || values.personalData.email.length === 0;
  };
  if (autoSubmitChecked !== true) {
    return <Loader/>;
  }
  
  return (
    <Background
      displayName="Background"
      sizeInfo={{ height: 1200, width: 1200 }}
      addEditableBlock={addEditableBlock}
      dataTemplateConfig="screens.collect.background"
      backgroundImage={config.background.imageUrl}
      backgroundColor={config.background.color}
      isEdition={isEdition}
      onClick={onBlockEdit}
      className={className}
    >
      <CollectHtmlZone
        displayName="HTML Zone"
        addEditableBlock={addEditableBlock}
        html={config.mainTextBox.html}
        dataTemplateConfig="screens.collect.mainTextBox"
        isEdition={isEdition}
        onClick={onBlockEdit}
      />
      <Formik
        initialValues={{
          consents: {},
          personalData: {
            email: retailerPersonalData === null || retailerPersonalData.email === null
              ? ''
              : retailerPersonalData.email,
          },
        }}
        enableReinitialize
        validationSchema={CollectScreenSchema}
        validateOnChange={true}
        onSubmit={(values, { setSubmitting }): void => {
          if (onSubmit !== undefined && userConsents.find(({ type }) => type === 'toggle')) {
            const optinsAccepted: Record<string, boolean> = {};
            for (const key of optinsKeysAccepted) {
              optinsAccepted[key] = true;
            }
            values.consents = optinsAccepted;			
            onSubmit(values);
            return;
          }
          if (onSubmit !== undefined) {
            onSubmit(values);
            return;
          }
          if (ctaOnClick !== undefined) {
            ctaOnClick();
          }
          setSubmitting(false);
        }}
      >
        {({ handleSubmit, setFieldValue, values, isSubmitting }: FormikProps<CollectFormValues>): React.ReactNode => <Form
          onKeyDown={(keyEvent): void => {
            if (keyEvent.key === 'Enter') {
              keyEvent.preventDefault();
            }
          }}
        >
          <PersonalData
            suOptinForm={suOptinForm}
            displayName="Personal Data"
            dataTemplateConfig="screens.collect.personalData"
            consentsConfig={userConsents}
            personalDataConfig={config.personalData}
            isEdition={isEdition}
            addEditableBlock={addEditableBlock}
            onBlockEdit={onBlockEdit}
            availableData={{ email: retailerPersonalData?.email, phone: retailerPersonalData?.phone }}
          />
          <Consents
            suOptinForm={suOptinForm}
            disabled={false}
            isEdition={isEdition}
            consentsConfig={userConsents}
            onBlockEdit={onBlockEdit}
            dataTemplateConfig="screens.collect.consents"
            displayName="Consents"
            checked={(evt): boolean => isOptinChecked(evt.target.checked)}
            onOptinsKeysAcceptedChange={handleOptinsKeysAcceptedChange}
          />
          <CtaButton
            hide={isHiddenForCheckbox()}
            disabled={isSubmitting || isConsentsDisabled(values)}
            dataTemplateType="accept-cta-button"
            displayName="Accept CTA"
            addEditableBlock={addEditableBlock}
            backgroundColor={config.acceptCtaButton.background.color}
            innerHTML={config.acceptCtaButton.html}
            onClick={
              onBlockEdit === undefined
                ? (): void => handleAcceptCtaClick({ handleSubmit, setFieldValue })
                : onBlockEdit
            }
            dataTemplateConfig="screens.collect.acceptCtaButton"
            isEdition={isEdition}
          />
          {
            config.declineCtaButton.hide !== true &&
            <DeclineCtaButton
              disabled={isSubmitting || isConsentsDisabled(values)}
              dataTemplateType="decline-cta-button"
              displayName="Opt-out CTA"
              addEditableBlock={addEditableBlock}
              innerHTML={config.declineCtaButton.html}
              onClick={
                onBlockEdit === undefined
                  ? (): void => handleDeclineCtaClick({ handleSubmit, setFieldValue })
                  : onBlockEdit
              }
              dataTemplateConfig="screens.collect.declineCtaButton"
              isEdition={isEdition}
              backgroundColor={config.declineCtaButton.background.color}
            />
          }
        </Form>
        }
      </Formik>
    </Background>
  );
};
